import { useState, useRef } from "react";
import {
  Avatar,
  Box,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SubTaskIcon from "../../assets/action-items/sub-tasks-icon.svg";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AddtaskPlusIcon from "../../assets/action-items/Action-item-plus-icon.svg";
import AddIcon from "../../assets/action-items/add-attachment.svg";
import ManualIcon from "../../assets/action-items/manual-icon.svg";
import ActionItemsAiIcon from "../../assets/interaction-detail-page/actionitems_ai-icon.svg";
import CalendarIcon from "../../assets/action-items/due-date-calendar-icon.svg";
import ActionItemsStatusCell from "./ActionItemsStatusCell";
import UserSelector from "./UserSelector";
import CommonDatePicker from "../Common/CommonDatePicker";
import {
  getAvatarColor,
  getAvatarInitials,
} from "../../utils/utils";
import { ActionItemBreadcrumb, SubtaskItem } from "../../utils/types";

dayjs.extend(utc);

export type { ActionItemBreadcrumb, SubtaskItem };
export {
  normalizeSubtasks,
  getParentActionItem,
  getAncestorBreadcrumbs,
} from "../../utils/subtaskUtils";
export { ActionItemBreadcrumbs } from "../../utils/ActionItemBreadcrumbs";

const ActionItemSubtasks = ({
  subtasks,
  canAdd,
  isAdding,
  isTask = true,
  newTitle,
  onNewTitleChange,
  newAssignee = null,
  onNewAssigneeChange = () => {},
  newDueDate = null,
  onNewDueDateChange = () => {},
  newStatus = "to_do",
  onNewStatusChange = () => {},
  users = [],
  onStartAdd,
  onCancelAdd,
  onSaveAdd,
  onOpenSubtask,
}: {
  subtasks: SubtaskItem[];
  canAdd: boolean;
  isAdding: boolean;
  isTask?: boolean;
  newTitle: string;
  onNewTitleChange: (value: string) => void;
  newAssignee?: string | null;
  onNewAssigneeChange?: (value: string | null) => void;
  newDueDate?: string | null;
  onNewDueDateChange?: (value: string | null) => void;
  newStatus?: string;
  onNewStatusChange?: (value: string) => void;
  users?: any[];
  onStartAdd: () => void;
  onCancelAdd: () => void;
  onSaveAdd: () => void;
  onOpenSubtask: (subtask: SubtaskItem) => void;
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const dateAnchorRef = useRef<HTMLDivElement | null>(null);

  const effectiveCanAdd = canAdd && isTask;
  const hasSubtasks = subtasks.length > 0;
  const showSection = hasSubtasks || effectiveCanAdd;
  if (!showSection) return null;

  return (
    <Box className="action-item-subtasks">
      <Box className="action-item-subtasks__header">
        <Typography className="field-label">Subtasks</Typography>
        {effectiveCanAdd && !hasSubtasks && !isAdding && (
          <Tooltip title="Add Sub Task" placement="top" arrow>
            <IconButton
              onClick={onStartAdd}
              size="small"
              disableRipple
            >
              <img src={AddIcon} alt="Add" className="add-icon" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {(subtasks.length > 0 || isAdding) && (
            <Box className="action-item-subtasks__table">
              <Box className="action-item-subtasks__row action-item-subtasks__row--header">
                <Typography>Title</Typography>
                <Typography>Solo Player</Typography>
                <Typography>Due Date</Typography>
                <Typography>Status</Typography>
              </Box>
              {subtasks.map((subtask) => {
                const due = subtask.dueDate
                  ? dayjs(subtask.dueDate).utc(true)
                  : null;
                return (
                  <Box
                    key={(subtask as any).rowKey || subtask.actionItemId}
                    className={`action-item-subtasks__row action-item-subtasks__row--body ${!subtask.actionItemId ? "action-item-subtasks__row--no-pointer" : ""}`}
                    onClick={() => {
                      if (subtask.actionItemId) onOpenSubtask(subtask);
                    }}
                  >
                    <Box className="action-item-subtasks__title">
                      <img
                        src={SubTaskIcon}
                        alt=""
                        className="action-item-subtasks__icon"
                      />
                      <img
                        src={subtask.isAiGenerated ? ActionItemsAiIcon : ManualIcon}
                        alt={subtask.isAiGenerated ? "AI" : "Manual"}
                      />
                      <Tooltip title={subtask.title} arrow placement="top">
                        <Typography>{subtask.title || "Untitled"}</Typography>
                      </Tooltip>
                    </Box>
                    <Box className="action-item-subtasks__assignee">
                      {subtask.assigneeName || subtask.assigneeEmail ? (
                        <Tooltip
                          title={subtask.assigneeName || subtask.assigneeEmail || ""}
                          arrow
                          placement="top"
                        >
                          <Avatar
                            src={subtask.assigneePictureUrl || undefined}
                            className="action-item-subtasks__avatar"
                            sx={{
                              bgcolor: getAvatarColor(
                                subtask.assigneeName || subtask.assigneeEmail,
                                subtask.assigneeEmail
                              ),
                            }}
                          >
                            {getAvatarInitials(
                              subtask.assigneeName || subtask.assigneeEmail,
                              subtask.assigneeEmail
                            )}
                          </Avatar>
                        </Tooltip>
                      ) : (
                        <Typography className="action-item-subtasks__empty">—</Typography>
                      )}
                    </Box>
                    <Typography className="action-item-subtasks__date">
                      {due?.isValid() ? due.format("MMM DD, YYYY") : "—"}
                    </Typography>
                    <Box onClick={(e) => e.stopPropagation()}>
                      <ActionItemsStatusCell
                        value={subtask.status || "to_do"}
                        canEdit={false}
                        onChange={() => {}}
                        noBorder
                      />
                    </Box>
                  </Box>
                );
              })}
              {isAdding && (
                <Box
                  className="action-item-subtasks__row action-item-subtasks__row--add"
                >
                  {/* Title Column */}
                  <Box className="action-item-subtasks__title">
                    <img
                      src={SubTaskIcon}
                      alt=""
                      className="action-item-subtasks__icon"
                    />
                    <InputBase
                      autoFocus
                      className="subtask-cell-box action-item-subtasks__input"
                      placeholder="Task title"
                      value={newTitle}
                      onChange={(e) => onNewTitleChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          onSaveAdd();
                        }
                        if (e.key === "Escape") onCancelAdd();
                      }}
                    />
                  </Box>

                  {/* Assignee Column */}
                  <Box className="action-item-subtasks__assignee">
                    <Box className="subtask-cell-box subtask-cell-box--assignee">
                      <UserSelector
                        value={newAssignee}
                        onChange={(val) => onNewAssigneeChange(val as string | null)}
                        users={users}
                        placeholder="Assignee"
                        variant="avatarOnly"
                      />
                    </Box>
                  </Box>

                  {/* Due Date Column */}
                  <Box className="action-item-subtasks__date">
                    <Box
                      ref={dateAnchorRef}
                      onClick={() => setOpenDatePicker(true)}
                      className="subtask-cell-box subtask-cell-box--date"
                    >
                      <Typography
                        className={`action-item-subtasks__date-text ${newDueDate ? "action-item-subtasks__date-text--active" : ""}`}
                      >
                        {newDueDate ? dayjs(newDueDate).format("MMM DD, YYYY") : "Select"}
                      </Typography>
                      <img src={CalendarIcon} alt="calendar" className="action-item-subtasks__calendar-icon" />
                    </Box>
                    <CommonDatePicker
                      value={newDueDate}
                      onChange={(newDate) => {
                        const formatted = newDate ? dayjs(newDate).format("YYYY-MM-DD") : null;
                        onNewDueDateChange(formatted);
                      }}
                      onClose={() => setOpenDatePicker(false)}
                      anchorEl={openDatePicker ? dateAnchorRef.current : null}
                      saveAsDateOnly
                    />
                  </Box>

                  {/* Status Column */}
                  <Box className="action-item-subtasks__status">
                    <Box className="subtask-cell-box subtask-cell-box--status">
                      <ActionItemsStatusCell
                        value={newStatus || "to_do"}
                        canEdit={true}
                        onChange={(val) => onNewStatusChange(val)}
                        noBorder
                        minWidth="0"
                      />
                    </Box>
                    <Box className="action-item-subtasks__actions">
                      <Tooltip title="Save" arrow placement="top">
                        <Box
                          onClick={onSaveAdd}
                          className="action-item-subtasks__action-btn action-item-subtasks__action-btn--save"
                        >
                          <CheckIcon className="action-item-subtasks__action-icon" />
                        </Box>
                      </Tooltip>
                      <Tooltip title="Cancel" arrow placement="top">
                        <Box
                          onClick={onCancelAdd}
                          className="action-item-subtasks__action-btn action-item-subtasks__action-btn--cancel"
                        >
                          <CloseIcon className="action-item-subtasks__action-icon" />
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
          {effectiveCanAdd && !isAdding && hasSubtasks && (
            <Box
              className="add-task-row flex flex-row items-center cursor-pointer"
              onClick={onStartAdd}
            >
              <span className="add-task-row-text flex items-center gap-1">
                <img src={AddtaskPlusIcon} alt="plus" className="add-task-plus-icon" />
                Add Sub Task
              </span>
            </Box>
          )}
    </Box>
  );
};

export default ActionItemSubtasks;
